/* ==========================================================
   LQK Good Deeds Tracker — v2
   assets/good-deeds.js

   WHAT CHANGED FROM v1, AND WHY IT MATTERS
   ─────────────────────────────────────────
   v1 kept everything in localStorage and nowhere else. Safari
   deletes script-writable storage after about seven days without
   a visit, so families silently lost months of history and there
   was no copy to restore.

   v2 treats the LQK-Deeds service as the source of truth and
   localStorage as nothing more than a cache, so:
     - data follows a family across devices,
     - Safari clearing the cache costs nothing,
     - and there is an export button, so a family can always hold
       their own copy.

   Two design rules run through the whole file:

   1. A TAP MUST FEEL INSTANT. The parent using this is tired and
      one-handed at bedtime. Every action updates the screen first
      and reaches the network afterwards; if the request fails the
      change is rolled back and said out loud.

   2. NOTHING IS EVER LOST TO A BAD CONNECTION. Actions that fail
      offline are queued and replayed, so a tap in a lift is not a
      tap thrown away.
   ========================================================== */

(function () {
  'use strict';

  /**
   * Refuse to run twice.
   *
   * The template loads this file, and a theme that still carries the old
   * site-wide tag in theme.liquid loads it again — so on the tracker page
   * it can execute TWICE. That is not a cosmetic problem: boot() attaches
   * a DELEGATED click listener to #gd-app, and #gd-sheet lives inside
   * #gd-app, so two listeners see the same bubbling click and every action
   * in a sheet fires twice. Two children per submit, doubled rewards,
   * doubled custom deeds. Disabling the button cannot help, because both
   * handlers run before the disable lands.
   *
   * One flag makes the second include a no-op, so the tracker is correct
   * whether or not the old tag has been cleaned out of the layout.
   */
  if (window.__gdBooted) return;
  window.__gdBooted = true;

  var CONFIG = window.GD_CONFIG || {};
  var CACHE_KEY = 'gd_v2_cache_' + (CONFIG.customerId || 'guest');
  var QUEUE_KEY = 'gd_v2_queue_' + (CONFIG.customerId || 'guest');

  /* ── State ───────────────────────────────────────────────── */

  var state = null;          /* the server snapshot */
  var currentChildId = null;
  var offline = false;
  var sheetReturnFocus = null;

  /* ── Tiny helpers ────────────────────────────────────────── */

  function $(id) { return document.getElementById(id); }

  /* Escape before interpolating. Child names and custom deed
     labels are parent-supplied text and go straight into HTML. */
  function esc(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function child(id) {
    if (!state) return null;
    for (var i = 0; i < state.children.length; i++) {
      if (state.children[i].id === id) return state.children[i];
    }
    return null;
  }

  /* Singapore time, matching the server's day boundary exactly. A
     mismatch here would put a 7am deed on the wrong day and break
     the streak the parent is looking at. */
  function todaySgt() {
    return new Date(Date.now() + 8 * 3600 * 1000).toISOString().slice(0, 10);
  }

  function plural(n, one, many) { return n === 1 ? one : many; }

  var STAR_SVG =
    '<svg class="gd-star" viewBox="0 0 24 24" aria-hidden="true">' +
    '<path d="M12 2.5l2.9 6.06 6.6.86-4.8 4.6 1.2 6.58L12 17.5l-5.9 3.1 1.2-6.58-4.8-4.6 6.6-.86z"/></svg>';

  var TICK_SVG =
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9.6 16.2l-3.8-3.8-1.4 1.4 5.2 5.2 10-10-1.4-1.4z"/></svg>';

  var CAMERA_SVG =
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 9a4 4 0 100 8 4 4 0 000-8zm8-3h-2.6l-1.2-1.6a1 1 0 00-.8-.4H8.6a1 1 0 00-.8.4L6.6 6H4a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2V8a2 2 0 00-2-2z"/></svg>';

  function relativeDay(iso) {
    var days = Math.floor((Date.parse(todaySgt()) - Date.parse(iso.slice(0, 10))) / 86400000);
    if (days <= 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return days + ' days ago';
    if (days < 14) return 'Last week';
    return Math.floor(days / 7) + ' weeks ago';
  }

  /* ── Toast ───────────────────────────────────────────────── */

  var toastTimer = null;

  function toast(message, isWarning) {
    var el = $('gd-toast');
    if (!el) return;
    el.textContent = message;
    el.className = 'gd-toast' + (isWarning ? ' gd-toast--warn' : '');
    el.setAttribute('data-show', 'true');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      el.setAttribute('data-show', 'false');
    }, isWarning ? 4200 : 2600);
  }

  /* ── Cache ───────────────────────────────────────────────── */
  /* Purely a paint-fast-on-open convenience. Never authoritative:
     whatever the server returns replaces it. */

  function readCache() {
    try {
      var raw = localStorage.getItem(CACHE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (err) { return null; }
  }

  function writeCache(snapshot) {
    try { localStorage.setItem(CACHE_KEY, JSON.stringify(snapshot)); }
    catch (err) { /* private mode, or full — the server still has it */ }
  }

  /* ── Offline queue ───────────────────────────────────────── */
  /* A tap made on a train must not evaporate. Failed writes are
     stored and replayed in order on the next successful contact. */

  function readQueue() {
    try {
      var raw = localStorage.getItem(QUEUE_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) { return []; }
  }

  function writeQueue(items) {
    try { localStorage.setItem(QUEUE_KEY, JSON.stringify(items)); }
    catch (err) { /* nothing more we can do */ }
  }

  function enqueue(method, route, body) {
    var queue = readQueue();
    /* Cap it: an endlessly retrying queue would grow without limit
       and eventually fail to save at all. */
    if (queue.length >= 200) queue.shift();
    queue.push({ method: method, route: route, body: body });
    writeQueue(queue);
  }

  function flushQueue() {
    var queue = readQueue();
    if (!queue.length) return Promise.resolve(false);

    function step(index) {
      if (index >= queue.length) {
        writeQueue([]);
        return Promise.resolve(true);
      }
      var item = queue[index];
      return request(item.method, item.route, item.body, true)
        .then(function () { return step(index + 1); })
        .catch(function () {
          /* Still unreachable — keep what is left for next time. */
          writeQueue(queue.slice(index));
          return false;
        });
    }

    return step(0);
  }

  /* ── Network ─────────────────────────────────────────────── */

  function request(method, route, body, isReplay) {
    if (!CONFIG.api || !CONFIG.token) {
      return Promise.reject(new Error('tracker is not configured'));
    }

    var options = {
      method: method,
      headers: { Authorization: 'Bearer ' + CONFIG.token }
    };
    if (body) {
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body);
    }

    /* `name` rides along on every call so the server can label who did
       what inside a shared tracker ("Marked by Abu"). It is cosmetic —
       the HMAC token is the only thing that authorises anything. */
    var separator = route.indexOf('?') === -1 ? '?' : '&';
    var url = CONFIG.api + route + separator +
      'shop=' + encodeURIComponent(CONFIG.shop || '') +
      '&name=' + encodeURIComponent(CONFIG.firstName || '');

    return fetch(url, options).then(function (response) {
      if (!response.ok) {
        return response.json().catch(function () { return {}; }).then(function (payload) {
          var error = new Error(payload.error || ('request failed: ' + response.status));
          error.status = response.status;
          throw error;
        });
      }
      setOffline(false);
      return response.json();
    }).catch(function (err) {
      /* A TypeError from fetch means the network never carried it —
         distinct from the server answering with an error. Only the
         former is worth queueing and calling "offline". */
      if (!err.status && !isReplay) setOffline(true);
      throw err;
    });
  }

  function setOffline(value) {
    if (offline === value) return;
    offline = value;
    var banner = $('gd-banner');
    if (!banner) return;
    if (value) {
      banner.textContent = 'You are offline. Taps are saved on this device and will sync when you are back.';
      banner.classList.remove('gd-hidden');
    } else {
      banner.classList.add('gd-hidden');
    }
  }

  /**
   * Apply a server response that carries a fresh snapshot.
   *
   * `patchOnly` updates the changing numbers in place instead of
   * re-rendering — used for marking and undoing deeds, where a full
   * re-render would rebuild the deed grid the parent is tapping.
   */
  function absorb(payload, patchOnly) {
    if (payload && payload.state) {
      state = payload.state;
      writeCache(state);
      if (patchOnly && currentChildId && child(currentChildId)) patchChild();
      else render();
    }
    return payload;
  }

  function absorbPatch(payload) { return absorb(payload, true); }

  /* ── Views ───────────────────────────────────────────────── */

  function render() {
    if (!state) return;
    if (currentChildId && !child(currentChildId)) currentChildId = null;
    if (currentChildId) renderChild();
    else renderDashboard();
    revealIn();
  }

  var hasRevealed = false;

  function showAll() {
    var nodes = document.querySelectorAll('.gd-reveal:not(.is-in)');
    for (var i = 0; i < nodes.length; i++) nodes[i].classList.add('is-in');
  }

  /**
   * Fade the first paint in, and make every later paint instant.
   *
   * Two things this must get right:
   *
   * 1. ONLY THE FIRST RENDER ANIMATES. Marking a deed re-renders the
   *    view, and re-playing a page-wide fade on the most repeated
   *    action in the app would make it feel slower every single tap.
   *
   * 2. IT CAN NEVER LEAVE THE PAGE BLANK. `.gd-reveal` starts at
   *    opacity 0, so the class that reveals it is load-bearing. In a
   *    BACKGROUND TAB requestAnimationFrame does not fire at all —
   *    verified in testing — so the frame callback alone is not
   *    enough. The timeout is the failsafe that guarantees content
   *    appears whether or not a frame is ever painted.
   */
  function revealIn() {
    if (hasRevealed) { showAll(); return; }
    hasRevealed = true;

    /* Two frames: one for the browser to lay the nodes out, one for
       the transition to have a start value to animate from. */
    requestAnimationFrame(function () {
      requestAnimationFrame(showAll);
    });

    setTimeout(showAll, 1600);
  }

  /* ── Dashboard ───────────────────────────────────────────── */

  function renderDashboard() {
    var root = $('gd-main');
    if (!root) return;

    var name = CONFIG.firstName ? esc(CONFIG.firstName) : 'there';
    var html =
      '<div class="gd-greeting gd-reveal">' +
        '<h1>Assalamualaikum, ' + name + '.</h1>' +
      '</div>';

    if (!state.children.length) {
      html +=
        '<div class="gd-empty gd-reveal">' +
          '<h2>Add your first child.</h2>' +
          '<p>Track the good deeds they do each day, and set a reward they can work towards together.</p>' +
          '<button class="gd-btn" data-action="add-child">Add a child</button>' +
        '</div>' +
        /* Shown even with no children: a parent who was sent a code by
           their spouse arrives here first, and the code is the whole
           reason they opened the page. Hiding it behind "add a child"
           would have them create a second, separate tracker instead. */
        householdSectionHtml();
      root.innerHTML = html;
      return;
    }

    html += '<p class="gd-kicker gd-reveal">Your children</p><div class="gd-children gd-reveal">';

    state.children.forEach(function (kid) {
      var summary = kid.summary;
      var streak = summary.currentStreak;
      html +=
        '<button class="gd-child" data-action="open-child" data-child="' + esc(kid.id) + '">' +
          avatarHtml(kid, 'md') +
          '<span class="gd-child__body">' +
            '<span class="gd-child__name">' + esc(kid.name) + '</span>' +
            '<span class="gd-child__meta">' +
              '<span>' + summary.cycleStars + ' ' + plural(summary.cycleStars, 'star', 'stars') + '</span>' +
              (streak > 0 ? '<span>' + streak + '-day streak</span>' : '') +
            '</span>' +
          '</span>' +
          '<svg class="gd-child__chev" width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">' +
            '<path fill="currentColor" d="M9 6l6 6-6 6"/></svg>' +
        '</button>';
    });

    html += '</div>';

    html +=
      '<div class="gd-section gd-reveal">' +
        '<button class="gd-btn gd-btn--ghost gd-btn--block" data-action="add-child">Add another child</button>' +
      '</div>' +
      householdSectionHtml() +
      '<div class="gd-section gd-reveal">' +
        '<p class="gd-kicker">Your data</p>' +
        '<div class="gd-card">' +
          '<p class="gd-note" style="margin-top:0">Everything here is saved to your Little Quran Kids account, so it stays safe if you change phone or clear your browser. You can also keep your own copy.</p>' +
          '<button class="gd-btn gd-btn--ghost" data-action="export" style="margin-top:14px">Download a copy</button>' +
        '</div>' +
      '</div>';

    root.innerHTML = html;
  }

  /* ── Sharing with the other parent ───────────────────────── */

  /**
   * The household card on the dashboard.
   *
   * Deliberately states where the data lives before offering anything,
   * because "will I lose this again?" is the question every LQK parent
   * arrives with after v1.
   */
  function householdSectionHtml() {
    var household = state.household;
    if (!household) return ''; /* an old cached snapshot — the server fills it in */

    var others = household.members.filter(function (member) { return !member.isYou; });

    var body;
    if (others.length) {
      body =
        '<div class="gd-people">' + household.members.map(memberRowHtml).join('') + '</div>' +
        '<button class="gd-btn gd-btn--ghost gd-btn--block" data-action="share" style="margin-top:14px">' +
          'Manage sharing' +
        '</button>';
    } else {
      body =
        '<p class="gd-note" style="margin-top:0">' +
          'Add the other parent so you both mark deeds from your own phone, ' +
          'with your own login. You will both see the same children.' +
        '</p>' +
        '<button class="gd-btn gd-btn--ghost gd-btn--block" data-action="share" style="margin-top:14px">' +
          'Share with another parent' +
        '</button>';
    }

    return '<div class="gd-section gd-reveal">' +
        '<p class="gd-kicker">' + (others.length ? 'Shared with' : 'Another parent') + '</p>' +
        '<div class="gd-card">' + body + '</div>' +
      '</div>';
  }

  function memberRowHtml(member) {
    var label = member.name || 'Parent';
    return '<div class="gd-person">' +
        '<span class="gd-person__mark" aria-hidden="true">' +
          esc(label.trim().charAt(0).toUpperCase() || '?') +
        '</span>' +
        '<span class="gd-person__body">' +
          '<span class="gd-person__name">' + esc(label) + (member.isYou ? ' (you)' : '') + '</span>' +
          '<span class="gd-person__role">' +
            (member.role === 'owner' ? 'Set up this tracker' : 'Can mark deeds') +
          '</span>' +
        '</span>' +
      '</div>';
  }

  /**
   * The sharing sheet.
   *
   * Codes are shown to people already in the household and nowhere else.
   * The join field stays available to an owner as well as a newcomer: a
   * father who has been tracking on his own account may still be the one
   * who receives his wife's code.
   */
  function shareSheetHtml() {
    var household = state.household;
    var html =
      '<p class="gd-note" style="margin-top:0">' +
        'Everyone here signs in with their own Little Quran Kids account. ' +
        'Nobody shares a password, and every deed shows who marked it.' +
      '</p>' +
      '<div class="gd-people" style="margin-top:16px">' +
        household.members.map(function (member) {
          var row = memberRowHtml(member);
          if (household.isOwner && !member.isYou) {
            row = row.replace(
              '</div>',
              '<button class="gd-history__undo" data-action="remove-member" ' +
                'data-customer="' + esc(member.customerId) + '" ' +
                'data-name="' + esc(member.name || 'this parent') + '">Remove</button></div>'
            );
          }
          return row;
        }).join('') +
      '</div>';

    /* Active codes, newest first. */
    if (household.invites.length) {
      html += '<div class="gd-field" style="margin-top:20px">' +
        '<span class="gd-label">Codes waiting to be used</span>' +
        household.invites.map(function (invite) {
          return '<div class="gd-code">' +
              '<span class="gd-code__value">' + esc(invite.code) + '</span>' +
              '<span class="gd-code__actions">' +
                '<button class="gd-btn" data-action="send-code" ' +
                  'data-code="' + esc(invite.code) + '">Send</button>' +
                '<button class="gd-history__undo" data-action="cancel-invite" ' +
                  'data-code="' + esc(invite.code) + '">Cancel</button>' +
              '</span>' +
            '</div>' +
            '<p class="gd-note">Works once, and expires ' + esc(expiryPhrase(invite.expiresAt)) + '.</p>';
        }).join('') +
      '</div>';
    }

    if (household.members.length < household.maxMembers) {
      /* Once a code exists, SENDING it is the job — so the create button
         steps back to a ghost and leaves the one filled button on the
         screen pointing at what the parent actually came to do. */
      html +=
        '<button class="gd-btn ' + (household.invites.length ? 'gd-btn--ghost ' : '') +
          'gd-btn--block" data-action="create-invite" style="margin-top:16px">' +
          (household.invites.length ? 'Create another code' : 'Create an invite code') +
        '</button>';
    }

    /* Joining is offered to an owner only while their own tracker is still
       just theirs. Once they have a co-parent, leaving would hand that
       person a household its owner had walked out of — so the way out of a
       shared tracker is to remove the other parent, not to slip away into
       someone else's. A parent who was invited sees "Leave" instead. */
    if (household.isOwner && !household.isShared) {
      html +=
        '<div class="gd-sharejoin">' +
          '<p class="gd-kicker" style="margin-bottom:8px">Been given a code?</p>' +
          '<label class="gd-field" style="margin:0">' +
            '<span class="gd-sr">Invite code</span>' +
            '<input class="gd-input gd-input--code" id="gd-in-code" type="text" ' +
                   'maxlength="7" autocomplete="off" autocapitalize="characters" ' +
                   'spellcheck="false" placeholder="ABC123" />' +
          '</label>' +
          '<button class="gd-btn gd-btn--ghost gd-btn--block" data-action="join-family" ' +
                  'style="margin-top:10px">Join their tracker</button>' +
          (state.children.length
            ? '<p class="gd-note">' +
                'Your own tracker with ' + state.children.length + ' ' +
                plural(state.children.length, 'child', 'children') +
                ' is kept exactly as it is. You can come back to it at any time.' +
              '</p>'
            : '') +
        '</div>';
    } else if (!household.isOwner) {
      html +=
        '<div class="gd-sharejoin">' +
          '<button class="gd-btn gd-btn--danger gd-btn--block" data-action="leave-family">' +
            'Leave this tracker' +
          '</button>' +
          '<p class="gd-note">' +
            'You stop seeing these children. Nothing is deleted, and anything you ' +
            'marked stays with them.' +
          '</p>' +
        '</div>';
    }

    return html;
  }

  /** "in 7 days" reads better than a date nobody will check. */
  function expiryPhrase(iso) {
    var days = Math.round((Date.parse(iso) - Date.now()) / 86400000);
    if (days <= 0) return 'today';
    if (days === 1) return 'tomorrow';
    return 'in ' + days + ' days';
  }

  /**
   * Hand the code to the other parent.
   *
   * navigator.share is the right primitive here: this audience sends
   * things over WhatsApp, and the native sheet takes them straight there
   * instead of via a clipboard they then have to paste. Clipboard is the
   * fallback, and showing the code as selectable text is the fallback to
   * that — the code is never trapped behind an API that might be missing.
   */
  function sendCode(code) {
    var message =
      'Join our Good Deeds Tracker on Little Quran Kids.\n\n' +
      'Your code: ' + code + '\n\n' +
      'Sign in with your own account, open the tracker, and enter this code under ' +
      '"Been given a code?".\n' +
      window.location.origin + window.location.pathname;

    if (navigator.share) {
      navigator.share({ title: 'Good Deeds Tracker', text: message })
        .catch(function () { /* the parent dismissed the share sheet */ });
      return;
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(message)
        .then(function () { toast('Code copied. Paste it to the other parent.'); })
        .catch(function () { toast('Your code is ' + code + '.'); });
      return;
    }
    toast('Your code is ' + code + '.');
  }

  function avatarHtml(kid, size) {
    var tint = 'gd-avatar--tint-' + ((kid.avatar_idx || 0) % 4);
    if (kid.photo) {
      return '<span class="gd-avatar gd-avatar--' + size + ' ' + tint + '">' +
        '<img src="' + esc(kid.photo) + '" alt="" /></span>';
    }
    var initial = (kid.name || '?').trim().charAt(0).toUpperCase();
    return '<span class="gd-avatar gd-avatar--' + size + ' ' + tint + '" aria-hidden="true">' +
      esc(initial) + '</span>';
  }

  /* ── Child view ──────────────────────────────────────────── */

  /**
   * Today's tally per deed, so a parent can see at a glance what has
   * already been marked tonight.
   */
  function countsForToday(kid) {
    var today = todaySgt();
    var counts = {};
    kid.recent.forEach(function (entry) {
      if (entry.logged_on === today) {
        var key = entry.catalog_id || entry.label;
        counts[key] = (counts[key] || 0) + 1;
      }
    });
    return counts;
  }

  /**
   * Full render of the child view.
   *
   * The numbers that change on every tap live in identified SLOTS so
   * that marking a deed can patch them without rebuilding the deed
   * grid — see patchChild(). Rebuilding the grid on each tap replaced
   * all fourteen tiles under the parent's finger, which dropped taps
   * when several prayers were marked in quick succession.
   */
  function renderChild() {
    var root = $('gd-main');
    var kid = child(currentChildId);
    if (!root || !kid) return;

    var summary = kid.summary;
    var countsToday = countsForToday(kid);

    var html =
      '<button class="gd-back" data-action="dashboard">' +
        '<svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">' +
          '<path fill="currentColor" d="M15 6l-6 6 6 6"/></svg>' +
        'All children' +
      '</button>';

    /* Header */
    html +=
      '<div class="gd-greeting gd-reveal" style="align-items:center">' +
        avatarHtml(kid, 'lg') +
        '<div style="flex:1 1 auto;min-width:0;margin-left:14px">' +
          '<h1>' + esc(kid.name) + '</h1>' +
          '<p class="gd-note" style="margin-top:2px" id="gd-total-slot">' + totalLineHtml(summary) + '</p>' +
        '</div>' +
      '</div>';

    /* Reward — the hero. What the child is actually working towards. */
    html += '<div class="gd-reveal" id="gd-reward-slot">' + rewardCardHtml(summary) + '</div>';

    /* Stats */
    html += '<div class="gd-stats gd-reveal" id="gd-stats-slot">' + statsHtml(summary) + '</div>';

    /* Deeds — the only part deliberately NOT patched in place. */
    html +=
      '<div class="gd-section gd-reveal">' +
        '<p class="gd-kicker">Mark a good deed</p>' +
        '<div class="gd-deeds">' + deedTilesHtml(kid, countsToday) + '</div>' +
        '<button class="gd-btn gd-btn--quiet" data-action="add-deed" style="margin-top:12px">' +
          'Add your own deed' +
        '</button>' +
      '</div>';

    /* Fourteen-day strip */
    html +=
      '<div class="gd-section gd-reveal">' +
        '<p class="gd-kicker">The last two weeks</p>' +
        '<div class="gd-card" id="gd-strip-slot">' + stripHtml(summary) + '</div>' +
      '</div>';

    /* Rewards */
    html +=
      '<div class="gd-section gd-reveal">' +
        '<p class="gd-kicker">Rewards</p>' +
        '<div id="gd-rewards-slot">' + rewardsListHtml(kid) + '</div>' +
        '<button class="gd-btn gd-btn--ghost gd-btn--block" data-action="add-reward" style="margin-top:10px">' +
          'Set a reward' +
        '</button>' +
      '</div>';

    /* History */
    html += '<div class="gd-section gd-reveal" id="gd-history-slot">' + historyHtml(kid) + '</div>';

    /* Badges */
    html += '<div class="gd-section gd-reveal" id="gd-badges-slot">' + badgesHtml(summary) + '</div>';

    /* Manage */
    html +=
      '<div class="gd-section gd-reveal">' +
        '<p class="gd-kicker">Manage</p>' +
        '<div class="gd-card">' +
          '<div class="gd-row">' +
            '<button class="gd-btn gd-btn--ghost" data-action="edit-child">Edit profile</button>' +
            '<button class="gd-btn gd-btn--ghost" data-action="reset">Start fresh</button>' +
          '</div>' +
          '<p class="gd-note">Starting fresh sets the star count back to zero for the next reward. ' +
            'Nothing is deleted — the history and badges stay.</p>' +
          '<div style="margin-top:16px">' +
            '<button class="gd-btn gd-btn--danger gd-btn--block" data-action="remove-child">' +
              'Remove ' + esc(kid.name) + '’s profile' +
            '</button>' +
            '<p class="gd-note">This hides the profile. The history is kept and stays in your downloaded copy.</p>' +
          '</div>' +
        '</div>' +
      '</div>';

    root.innerHTML = html;
  }

  /* ── Slot builders ───────────────────────────────────────── */
  /* Each returns the innerHTML of one slot, so renderChild() and
     patchChild() cannot drift apart. */

  function totalLineHtml(summary) {
    return summary.totalStars + ' ' + plural(summary.totalStars, 'star', 'stars') + ' all time';
  }

  function statsHtml(summary) {
    return statHtml(summary.starsToday, 'Today') +
      statHtml(summary.starsWeek, 'This week') +
      statHtml(summary.currentStreak, 'Day streak');
  }

  function deedTilesHtml(kid, countsToday) {
    return kid.catalog.map(function (deed) {
      var count = countsToday[deed.id] || 0;
      return '<button class="gd-deed' + (count ? ' gd-deed--done' : '') + '" ' +
              'data-action="log" data-catalog="' + esc(deed.id) + '" ' +
              'data-cat="' + esc(deed.category) + '">' +
        '<span class="gd-deed__label">' + esc(deed.label) + '</span>' +
        '<span class="gd-deed__foot">' +
          '<span class="gd-deed__cat">' + esc(deed.category) + '</span>' +
          '<span class="gd-deed__count' + (count ? '' : ' gd-hidden') + '" data-count="' + esc(deed.id) + '">' +
            STAR_SVG + '<span data-count-value>' + count + '</span>' +
          '</span>' +
        '</span>' +
      '</button>';
    }).join('');
  }

  function stripHtml(summary) {
    var maxDay = summary.recentDays.reduce(function (max, day) {
      return day.stars > max ? day.stars : max;
    }, 0) || 1;

    var bars = summary.recentDays.map(function (day) {
      var height = day.stars ? Math.max(10, Math.round((day.stars / maxDay) * 48)) : 6;
      return '<span class="gd-strip__day' + (day.stars ? ' gd-strip__day--on' : '') + '" ' +
            'style="height:' + height + 'px" ' +
            'title="' + esc(day.date) + ': ' + day.stars + ' ' +
              plural(day.stars, 'star', 'stars') + '"></span>';
    }).join('');

    return '<div class="gd-strip">' + bars + '</div>' +
      '<p class="gd-note">' + summary.starsMonth + ' ' +
        plural(summary.starsMonth, 'star', 'stars') + ' in the last 30 days.</p>';
  }

  function historyHtml(kid) {
    if (!kid.recent.length) return '';

    /* Only worth the ink once two parents are marking deeds. On a
       single-parent tracker every row would say the reader's own name. */
    var shared = Boolean(state.household && state.household.isShared);

    var rows = kid.recent.slice(0, 12).map(function (entry) {
      var when = relativeDay(entry.logged_on);
      if (shared && entry.actor_name) when += ' · ' + entry.actor_name;
      return '<div class="gd-history__item">' +
          '<span class="gd-history__label">' + esc(entry.label) + '</span>' +
          '<span class="gd-history__when">' + esc(when) + '</span>' +
          '<button class="gd-history__undo" data-action="undo" data-log="' + esc(entry.id) + '" ' +
                  'aria-label="Undo ' + esc(entry.label) + '">Undo</button>' +
        '</div>';
    }).join('');
    return '<p class="gd-kicker">Recently marked</p>' +
      '<div class="gd-card"><div class="gd-history">' + rows + '</div></div>';
  }

  function badgesHtml(summary) {
    var cards = summary.badges.map(function (badge) {
      return '<div class="gd-badge' + (badge.earned ? '' : ' gd-badge--locked') + '">' +
          '<div class="gd-badge__mark">' +
            (badge.earned ? TICK_SVG : STAR_SVG.replace('gd-star', '')) +
          '</div>' +
          '<p class="gd-badge__name">' + esc(badge.name) + '</p>' +
          '<p class="gd-badge__desc">' + esc(badge.desc) + '</p>' +
        '</div>';
    }).join('');
    return '<p class="gd-kicker">Badges — ' + summary.badgesEarned + ' of ' +
      summary.badges.length + '</p><div class="gd-badges">' + cards + '</div>';
  }

  /**
   * Update the numbers after a deed is marked, WITHOUT touching the
   * deed grid.
   *
   * Marking a deed is the most repeated action in the app. A full
   * re-render replaced every tile, which threw away the node the
   * parent was touching — taps were dropped when marking several
   * prayers in a row. Here the tiles stay put and only their counts
   * are adjusted.
   */
  function patchChild() {
    var kid = child(currentChildId);
    if (!kid) return;

    var summary = kid.summary;
    var counts = countsForToday(kid);

    var slots = {
      'gd-total-slot': totalLineHtml(summary),
      'gd-reward-slot': rewardCardHtml(summary),
      'gd-stats-slot': statsHtml(summary),
      'gd-strip-slot': stripHtml(summary),
      'gd-rewards-slot': rewardsListHtml(kid),
      'gd-history-slot': historyHtml(kid),
      'gd-badges-slot': badgesHtml(summary)
    };

    Object.keys(slots).forEach(function (id) {
      var el = $(id);
      if (el) el.innerHTML = slots[id];
    });

    /* Reconcile each tile's count with the server's answer, in place. */
    kid.catalog.forEach(function (deed) {
      var count = counts[deed.id] || 0;
      var counter = document.querySelector('[data-count="' + deed.id + '"]');
      if (!counter) return;
      var valueEl = counter.querySelector('[data-count-value]');
      if (valueEl) valueEl.textContent = count;
      counter.classList.toggle('gd-hidden', count === 0);
      var tile = counter.closest('.gd-deed');
      if (tile) tile.classList.toggle('gd-deed--done', count > 0);
    });
  }

  function statHtml(value, label) {
    return '<div class="gd-stat">' +
      '<span class="gd-stat__num">' + value + '</span>' +
      '<span class="gd-stat__label">' + esc(label) + '</span>' +
    '</div>';
  }

  function rewardCardHtml(summary) {
    if (!summary.target) {
      return '<div class="gd-reward">' +
        '<div class="gd-reward__empty">' +
          '<div>' +
            '<h2>No reward set yet.</h2>' +
            '<p class="gd-reward__sub" style="margin:4px 0 0">' +
              summary.availableStars + ' ' + plural(summary.availableStars, 'star', 'stars') + ' ready to spend.' +
            '</p>' +
          '</div>' +
          '<button class="gd-btn" data-action="add-reward">Set one</button>' +
        '</div>' +
      '</div>';
    }

    var target = summary.target;
    var percent = Math.round(target.progress * 100);

    return '<div class="gd-reward' + (target.reached ? ' gd-reward--reached' : '') + '">' +
      '<h2>' + esc(target.label) + '</h2>' +
      '<p class="gd-reward__sub">Working towards this reward.</p>' +
      '<div class="gd-reward__track" role="progressbar" aria-valuemin="0" aria-valuemax="100" ' +
           'aria-valuenow="' + percent + '" aria-label="Progress towards ' + esc(target.label) + '">' +
        '<div class="gd-reward__fill" style="width:' + percent + '%"></div>' +
      '</div>' +
      '<div class="gd-reward__row">' +
        '<span class="gd-reward__count">' + summary.availableStars + ' / ' + target.cost_stars + '</span>' +
        '<span class="gd-reward__left">' +
          (target.reached
            ? 'Reward earned.'
            : target.remaining + ' more ' + plural(target.remaining, 'star', 'stars') + ' to go.') +
        '</span>' +
      '</div>' +
      (target.reached
        ? '<button class="gd-btn gd-btn--block" data-action="give-reward" data-reward="' + esc(target.id) + '" ' +
                  'style="margin-top:16px">Mark as given</button>'
        : '') +
    '</div>';
  }

  function rewardsListHtml(kid) {
    if (!kid.rewards.length) {
      return '<div class="gd-card"><p class="gd-note" style="margin:0">' +
        'No rewards yet. Set something ' + esc(kid.name) + ' would love to work towards.' +
      '</p></div>';
    }

    var html = '<div class="gd-rewards">';
    kid.rewards.forEach(function (reward) {
      var isGiven = Boolean(reward.given_at);
      html +=
        '<div class="gd-rewardrow' + (isGiven ? ' gd-rewardrow--given' : '') + '">' +
          '<div class="gd-rewardrow__body">' +
            '<p class="gd-rewardrow__label">' + esc(reward.label) + '</p>' +
            '<p class="gd-rewardrow__cost">' + reward.cost_stars + ' ' +
              plural(reward.cost_stars, 'star', 'stars') +
              (isGiven ? ' — given' : '') + '</p>' +
          '</div>' +
          (isGiven
            ? ''
            : '<button class="gd-history__undo" data-action="remove-reward" data-reward="' + esc(reward.id) + '" ' +
                      'aria-label="Remove ' + esc(reward.label) + '">Remove</button>') +
        '</div>';
    });
    return html + '</div>';
  }

  /* ── Sheets ──────────────────────────────────────────────── */

  function openSheet(title, bodyHtml, onMount) {
    var sheet = $('gd-sheet');
    var panel = $('gd-sheet-panel');
    if (!sheet || !panel) return;

    sheetReturnFocus = document.activeElement;

    panel.innerHTML =
      '<div class="gd-sheet__grip" aria-hidden="true"></div>' +
      '<h2 class="gd-sheet__title">' + esc(title) + '</h2>' +
      bodyHtml;

    sheet.classList.remove('gd-hidden');

    /* One frame before flipping the flag, so the panel has its
       off-screen transform to animate FROM.

       The timeout is a failsafe, not decoration: requestAnimationFrame
       does not fire in a background tab, and a sheet left at
       data-open="false" is an INVISIBLE full-screen scrim that
       silently swallows every tap on the page. Both paths are
       idempotent, so whichever runs first wins harmlessly. */
    function open() {
      if (sheet.getAttribute('data-open') === 'true') return;
      sheet.setAttribute('data-open', 'true');
      var focusable = panel.querySelector('input, select, button:not([data-action="close-sheet"])');
      if (focusable) focusable.focus();
    }

    requestAnimationFrame(open);
    setTimeout(open, 80);

    if (onMount) onMount(panel);
  }

  function closeSheet() {
    var sheet = $('gd-sheet');
    if (!sheet || sheet.classList.contains('gd-hidden')) return;
    sheet.setAttribute('data-open', 'false');

    /* Wait for the slide-out before unmounting, or the sheet
       vanishes instead of leaving. */
    setTimeout(function () {
      sheet.classList.add('gd-hidden');
      var panel = $('gd-sheet-panel');
      if (panel) panel.innerHTML = '';
      if (sheetReturnFocus && sheetReturnFocus.focus) sheetReturnFocus.focus();
      sheetReturnFocus = null;
    }, 280);
  }

  function categoryOptions(selected) {
    return (state.categories || []).map(function (cat) {
      return '<option value="' + esc(cat) + '"' + (cat === selected ? ' selected' : '') + '>' +
        esc(cat) + '</option>';
    }).join('');
  }

  /* ── Photos ──────────────────────────────────────────────── */
  /* Downscaled in the browser before it ever leaves the device: a
     phone photo is several megabytes and the parent is likely on
     mobile data. 240px is ample for an 80px avatar at 3x. */

  var pendingPhoto = null;

  function compressPhoto(file, done) {
    var reader = new FileReader();
    reader.onload = function (event) {
      var image = new Image();
      image.onload = function () {
        var MAX = 240;
        var width = image.width;
        var height = image.height;
        var scale = Math.min(1, MAX / Math.max(width, height));
        width = Math.round(width * scale);
        height = Math.round(height * scale);

        var canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(image, 0, 0, width, height);
        done(canvas.toDataURL('image/jpeg', 0.78));
      };
      image.onerror = function () { done(null); };
      image.src = event.target.result;
    };
    reader.onerror = function () { done(null); };
    reader.readAsDataURL(file);
  }

  function childFormHtml(kid) {
    var name = kid ? esc(kid.name) : '';
    var age = kid && kid.age ? esc(kid.age) : '';
    var tintIdx = kid ? (kid.avatar_idx || 0) % 4 : 0;
    var photo = kid && kid.photo ? kid.photo : null;

    var tints = ['var(--tint-mint)', 'var(--tint-sky)', 'var(--tint-rose)', 'var(--sand)'];
    var tintButtons = tints.map(function (colour, index) {
      return '<button type="button" class="gd-tint" data-tint="' + index + '" ' +
        'style="background:' + colour + '" aria-pressed="' + (index === tintIdx) + '" ' +
        'aria-label="Colour ' + (index + 1) + '"></button>';
    }).join('');

    return '<div>' +
      '<label class="gd-field"><span class="gd-label">Name</span>' +
        '<input class="gd-input" id="gd-in-name" type="text" maxlength="40" ' +
               'autocomplete="off" value="' + name + '" placeholder="e.g. Amal" /></label>' +
      '<label class="gd-field"><span class="gd-label">Age <span style="font-weight:400;color:var(--muted)">(optional)</span></span>' +
        '<input class="gd-input" id="gd-in-age" type="number" min="1" max="18" ' +
               'inputmode="numeric" value="' + age + '" placeholder="e.g. 8" /></label>' +
      '<div class="gd-field"><span class="gd-label">Photo <span style="font-weight:400;color:var(--muted)">(optional)</span></span>' +
        '<div class="gd-photo">' +
          '<span class="gd-photo__preview" id="gd-photo-preview">' +
            (photo ? '<img src="' + esc(photo) + '" alt="" />' : CAMERA_SVG) +
          '</span>' +
          '<span class="gd-photo__actions">' +
            '<label class="gd-photo__btn" for="gd-in-photo">Choose photo</label>' +
            '<input type="file" id="gd-in-photo" accept="image/*" class="gd-sr" />' +
            (photo ? '<button type="button" class="gd-btn gd-btn--quiet" data-action="clear-photo">Remove photo</button>' : '') +
          '</span>' +
        '</div>' +
      '</div>' +
      '<div class="gd-field"><span class="gd-label">Colour</span>' +
        '<div class="gd-tints" id="gd-tints">' + tintButtons + '</div></div>' +
      '<button class="gd-btn gd-btn--block" data-action="' + (kid ? 'save-child-edit' : 'save-child-new') + '" ' +
              'data-child="' + (kid ? esc(kid.id) : '') + '">' +
        (kid ? 'Save changes' : 'Add child') +
      '</button>' +
    '</div>';
  }

  function mountChildForm(panel, kid) {
    pendingPhoto = kid && kid.photo ? kid.photo : null;

    var input = panel.querySelector('#gd-in-photo');
    if (input) {
      input.addEventListener('change', function () {
        var file = input.files && input.files[0];
        if (!file) return;
        compressPhoto(file, function (dataUri) {
          if (!dataUri) { toast('That image could not be read.', true); return; }
          pendingPhoto = dataUri;
          var preview = panel.querySelector('#gd-photo-preview');
          if (preview) preview.innerHTML = '<img src="' + esc(dataUri) + '" alt="" />';
        });
      });
    }

    var tints = panel.querySelector('#gd-tints');
    if (tints) {
      tints.addEventListener('click', function (event) {
        var button = event.target.closest('.gd-tint');
        if (!button) return;
        var all = tints.querySelectorAll('.gd-tint');
        for (var i = 0; i < all.length; i++) all[i].setAttribute('aria-pressed', 'false');
        button.setAttribute('aria-pressed', 'true');
      });
    }
  }

  function selectedTint(panel) {
    var active = panel.querySelector('.gd-tint[aria-pressed="true"]');
    return active ? Number(active.getAttribute('data-tint')) || 0 : 0;
  }

  /* ── Actions ─────────────────────────────────────────────── */

  /**
   * Mark a deed done.
   *
   * The count on the tile increments immediately and the request
   * follows. This is the single most-repeated action in the app and
   * it has to feel like the phone reacted, not like a form was
   * submitted. On failure the increment is rolled back — except
   * when the network is down, in which case the tap is queued and
   * the optimistic state is the honest one.
   */
  function logDeed(button) {
    var catalogId = button.getAttribute('data-catalog');
    var kid = child(currentChildId);
    if (!kid) return;

    var counter = document.querySelector('[data-count="' + catalogId + '"]');
    var valueEl = counter ? counter.querySelector('[data-count-value]') : null;
    var previous = valueEl ? Number(valueEl.textContent) || 0 : 0;

    if (valueEl) {
      valueEl.textContent = previous + 1;
      counter.classList.remove('gd-hidden');
      button.classList.add('gd-deed--done');
      /* Bump the count so the change is felt, not just seen. The
         class is removed on the next frame so a rapid second tap
         retargets the same transition rather than restarting it. */
      counter.classList.add('gd-deed__count--bumped');
      setTimeout(function () { counter.classList.remove('gd-deed__count--bumped'); }, 180);
    }

    request('POST', '/api/children/' + kid.id + '/deeds', { catalogId: catalogId })
      .then(absorbPatch)
      .catch(function (err) {
        if (!err.status) {
          /* Offline: keep the optimistic count and replay later. */
          enqueue('POST', '/api/children/' + kid.id + '/deeds', { catalogId: catalogId });
          return;
        }
        if (valueEl) {
          valueEl.textContent = previous;
          if (!previous) counter.classList.add('gd-hidden');
        }
        toast('That did not save. Please try again.', true);
      });
  }

  function withBusy(button, promise) {
    if (button) button.disabled = true;
    return promise.then(function (value) {
      if (button) button.disabled = false;
      return value;
    }).catch(function (err) {
      if (button) button.disabled = false;
      throw err;
    });
  }

  function handleAction(action, target) {
    var kid = child(currentChildId);

    switch (action) {

      case 'dashboard':
        currentChildId = null;
        render();
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;

      case 'open-child':
        currentChildId = target.getAttribute('data-child');
        render();
        window.scrollTo({ top: 0 });
        return;

      case 'log':
        logDeed(target);
        return;

      case 'undo':
        if (!kid) return;
        withBusy(target, request('DELETE', '/api/children/' + kid.id + '/deeds/' + target.getAttribute('data-log')))
          .then(absorbPatch)
          .then(function () { toast('Removed.'); })
          .catch(function () { toast('Could not undo that. Please try again.', true); });
        return;

      case 'add-child':
        openSheet('Add a child', childFormHtml(null), function (panel) {
          mountChildForm(panel, null);
        });
        return;

      case 'edit-child':
        if (!kid) return;
        openSheet('Edit profile', childFormHtml(kid), function (panel) {
          mountChildForm(panel, kid);
        });
        return;

      case 'clear-photo': {
        pendingPhoto = null;
        var preview = $('gd-photo-preview');
        if (preview) preview.innerHTML = CAMERA_SVG;
        return;
      }

      case 'save-child-new': {
        var newName = ($('gd-in-name') || {}).value || '';
        if (!newName.trim()) { toast('Please enter a name.', true); return; }
        var panelNew = $('gd-sheet-panel');
        withBusy(target, request('POST', '/api/children', {
          name: newName.trim(),
          age: Number(($('gd-in-age') || {}).value) || null,
          avatarIdx: selectedTint(panelNew),
          photo: pendingPhoto
        }))
          .then(absorb)
          .then(function (payload) {
            closeSheet();
            currentChildId = payload.child.id;
            render();
            toast(newName.trim() + ' has been added.');
          })
          .catch(function () { toast('Could not save that. Please try again.', true); });
        return;
      }

      case 'save-child-edit': {
        var editName = ($('gd-in-name') || {}).value || '';
        if (!editName.trim()) { toast('Please enter a name.', true); return; }
        var panelEdit = $('gd-sheet-panel');
        withBusy(target, request('PATCH', '/api/children/' + target.getAttribute('data-child'), {
          name: editName.trim(),
          age: Number(($('gd-in-age') || {}).value) || null,
          avatarIdx: selectedTint(panelEdit),
          photo: pendingPhoto
        }))
          .then(absorb)
          .then(function () { closeSheet(); toast('Profile updated.'); })
          .catch(function () { toast('Could not save that. Please try again.', true); });
        return;
      }

      case 'add-deed':
        if (!kid) return;
        openSheet('Add your own deed',
          '<label class="gd-field"><span class="gd-label">What is the deed?</span>' +
            '<input class="gd-input" id="gd-in-deed" type="text" maxlength="80" ' +
                   'placeholder="e.g. Helped with the washing up" /></label>' +
          '<label class="gd-field"><span class="gd-label">Category</span>' +
            '<select class="gd-select" id="gd-in-deed-cat">' + categoryOptions('Kindness') + '</select></label>' +
          '<button class="gd-btn gd-btn--block" data-action="save-deed">Add deed</button>');
        return;

      case 'save-deed': {
        if (!kid) return;
        var deedLabel = ($('gd-in-deed') || {}).value || '';
        if (!deedLabel.trim()) { toast('Please name the deed.', true); return; }
        withBusy(target, request('POST', '/api/children/' + kid.id + '/catalog', {
          label: deedLabel.trim(),
          category: ($('gd-in-deed-cat') || {}).value || 'Kindness'
        }))
          .then(absorb)
          .then(function () { closeSheet(); toast('Deed added.'); })
          .catch(function () { toast('Could not add that. Please try again.', true); });
        return;
      }

      case 'add-reward': {
        if (!kid) return;
        var suggestions = (state.suggestedRewards || []).map(function (item) {
          return '<option value="' + esc(item.label) + '" data-cost="' + item.cost + '">' +
            esc(item.label) + '</option>';
        }).join('');

        openSheet('Set a reward',
          '<label class="gd-field"><span class="gd-label">Pick a suggestion</span>' +
            '<select class="gd-select" id="gd-in-reward-pick">' +
              '<option value="">Choose one, or write your own below</option>' + suggestions +
            '</select></label>' +
          '<label class="gd-field"><span class="gd-label">Reward</span>' +
            '<input class="gd-input" id="gd-in-reward" type="text" maxlength="80" ' +
                   'placeholder="e.g. A trip to the park" /></label>' +
          '<label class="gd-field"><span class="gd-label">Stars needed</span>' +
            '<input class="gd-input" id="gd-in-cost" type="number" min="1" max="9999" ' +
                   'inputmode="numeric" value="10" style="max-width:140px" /></label>' +
          '<button class="gd-btn gd-btn--block" data-action="save-reward">Set reward</button>',
          function (panel) {
            /* Choosing a suggestion fills both fields, and the parent
               can still edit either — that is the "dropdown OR free
               text" requirement without two competing inputs. */
            var picker = panel.querySelector('#gd-in-reward-pick');
            picker.addEventListener('change', function () {
              var option = picker.options[picker.selectedIndex];
              if (!option.value) return;
              panel.querySelector('#gd-in-reward').value = option.value;
              panel.querySelector('#gd-in-cost').value = option.getAttribute('data-cost') || 10;
            });
          });
        return;
      }

      case 'save-reward': {
        if (!kid) return;
        var rewardLabel = ($('gd-in-reward') || {}).value || '';
        if (!rewardLabel.trim()) { toast('Please name the reward.', true); return; }
        var picked = ($('gd-in-reward-pick') || {}).value || '';
        withBusy(target, request('POST', '/api/children/' + kid.id + '/rewards', {
          label: rewardLabel.trim(),
          costStars: Number(($('gd-in-cost') || {}).value) || 10,
          source: picked && picked === rewardLabel.trim() ? 'suggested' : 'custom'
        }))
          .then(absorb)
          .then(function () { closeSheet(); toast('Reward set.'); })
          .catch(function () { toast('Could not save that. Please try again.', true); });
        return;
      }

      case 'give-reward':
        if (!kid) return;
        withBusy(target, request('POST',
          '/api/children/' + kid.id + '/rewards/' + target.getAttribute('data-reward') + '/give'))
          .then(absorb)
          .then(function () { toast('Reward marked as given. MashaAllah.'); })
          .catch(function () { toast('Could not save that. Please try again.', true); });
        return;

      case 'remove-reward':
        if (!kid) return;
        withBusy(target, request('DELETE',
          '/api/children/' + kid.id + '/rewards/' + target.getAttribute('data-reward')))
          .then(absorb)
          .then(function () { toast('Reward removed.'); })
          .catch(function () { toast('Could not remove that. Please try again.', true); });
        return;

      case 'reset':
        if (!kid) return;
        if (!window.confirm('Start fresh for ' + kid.name + '? The star count goes back to zero for the next reward. Nothing is deleted.')) return;
        withBusy(target, request('POST', '/api/children/' + kid.id + '/reset'))
          .then(absorb)
          .then(function () { toast('Started fresh.'); })
          .catch(function () { toast('Could not do that. Please try again.', true); });
        return;

      case 'remove-child':
        if (!kid) return;
        if (!window.confirm('Remove ' + kid.name + '’s profile? The history is kept and stays in your downloaded copy, but the profile will no longer appear here.')) return;
        withBusy(target, request('POST', '/api/children/' + kid.id + '/archive'))
          .then(absorb)
          .then(function () {
            currentChildId = null;
            render();
            toast('Profile removed.');
          })
          .catch(function () { toast('Could not do that. Please try again.', true); });
        return;

      /* ── Sharing ──────────────────────────────────────────── */

      case 'share':
        openSheet('Share this tracker', shareSheetHtml());
        return;

      case 'create-invite':
        withBusy(target, request('POST', '/api/family/invites'))
          .then(absorb)
          .then(function (payload) {
            /* Re-render the sheet in place rather than closing it: the
               parent asked for a code and the code is what they need to
               look at next. */
            var panel = $('gd-sheet-panel');
            if (panel) {
              panel.innerHTML =
                '<div class="gd-sheet__grip" aria-hidden="true"></div>' +
                '<h2 class="gd-sheet__title">Share this tracker</h2>' +
                shareSheetHtml();
              var fresh = panel.querySelector('.gd-code');
              if (fresh) fresh.classList.add('gd-code--new');
            }
            sendCode(payload.invite.code);
          })
          .catch(function (err) {
            toast(err.status ? err.message : 'Could not create a code. Please try again.', true);
          });
        return;

      case 'send-code':
        sendCode(target.getAttribute('data-code'));
        return;

      case 'cancel-invite':
        withBusy(target, request('DELETE',
          '/api/family/invites/' + encodeURIComponent(target.getAttribute('data-code'))))
          .then(absorb)
          .then(function () {
            closeSheet();
            toast('That code will no longer work.');
          })
          .catch(function () { toast('Could not cancel that code. Please try again.', true); });
        return;

      case 'join-family': {
        var codeInput = $('gd-in-code');
        var code = codeInput ? codeInput.value.trim() : '';
        if (!code) { toast('Please enter the code you were given.', true); return; }

        withBusy(target, request('POST', '/api/family/join', { code: code }))
          .then(absorb)
          .then(function () {
            closeSheet();
            currentChildId = null;
            render();
            window.scrollTo({ top: 0 });
            toast('You are in. You are both looking at the same tracker now.');
          })
          .catch(function (err) {
            toast(err.status ? err.message : 'Could not join just now. Please try again.', true);
          });
        return;
      }

      case 'leave-family':
        if (!window.confirm('Leave this tracker? You will stop seeing these children. Nothing is deleted, and your own tracker comes back exactly as you left it.')) return;
        withBusy(target, request('POST', '/api/family/leave'))
          .then(absorb)
          .then(function () {
            closeSheet();
            currentChildId = null;
            render();
            toast('You have left. This is your own tracker again.');
          })
          .catch(function (err) {
            toast(err.status ? err.message : 'Could not do that. Please try again.', true);
          });
        return;

      case 'remove-member': {
        var who = target.getAttribute('data-name') || 'this parent';
        if (!window.confirm('Remove ' + who + '? They stop seeing these children. Everything they marked stays, and nothing is deleted.')) return;
        withBusy(target, request('DELETE',
          '/api/family/members/' + encodeURIComponent(target.getAttribute('data-customer'))))
          .then(absorb)
          .then(function () { closeSheet(); toast(who + ' has been removed.'); })
          .catch(function (err) {
            toast(err.status ? err.message : 'Could not do that. Please try again.', true);
          });
        return;
      }

      case 'export':
        /* A plain link rather than fetch-and-blob: iOS Safari handles
           a real navigation to a download far more reliably. The token
           rides in the query string because an anchor cannot carry a
           header. */
        window.location.href = CONFIG.api + '/api/export?t=' + encodeURIComponent(CONFIG.token);
        toast('Your copy is downloading.');
        return;

      case 'close-sheet':
        closeSheet();
        return;
    }
  }

  /* ── Legacy rescue ───────────────────────────────────────── */

  /**
   * Push any surviving v1 localStorage data to the server, once.
   *
   * Most families lost theirs to Safari's eviction, but for anyone
   * whose browser still holds a copy this is the difference between
   * recovering months of history and starting from nothing. The
   * server refuses the import if the family already has children,
   * so this is safe to attempt on every load.
   */
  function rescueLegacy() {
    if (!CONFIG.legacyKey) return Promise.resolve(false);

    var legacy;
    try {
      var raw = localStorage.getItem(CONFIG.legacyKey);
      if (!raw) return Promise.resolve(false);
      legacy = JSON.parse(raw);
    } catch (err) {
      return Promise.resolve(false);
    }
    if (!legacy || !Array.isArray(legacy.children) || !legacy.children.length) {
      return Promise.resolve(false);
    }

    return request('POST', '/api/legacy-import', { legacy: legacy })
      .then(absorb)
      .then(function (payload) {
        if (payload && payload.imported) {
          toast('We found your old records on this device and saved them to your account.');
          return true;
        }
        return false;
      })
      .catch(function () { return false; });
  }

  /* ── Boot ────────────────────────────────────────────────── */

  function showFatal(message) {
    var root = $('gd-main');
    if (!root) return;
    root.innerHTML =
      '<div class="gd-empty">' +
        '<h2>The tracker could not load.</h2>' +
        '<p>' + esc(message) + '</p>' +
        '<button class="gd-btn" onclick="window.location.reload()">Try again</button>' +
      '</div>';
  }

  function boot() {
    var app = $('gd-app');
    if (!app) return; /* not the tracker page — this file loads site-wide */

    /**
     * ONE delegated listener for the whole app, including the sheet.
     *
     * It must stay one. #gd-sheet lives INSIDE #gd-app, so a second
     * listener on the sheet sees the same bubbling click and every
     * action inside a sheet fires twice — which created two children
     * per submit, and would have doubled every reward and custom deed.
     * Disabling the button does not help: both handlers run before the
     * disable takes effect.
     */
    app.addEventListener('click', function (event) {
      var target = event.target.closest('[data-action]');
      if (target) {
        event.preventDefault();
        handleAction(target.getAttribute('data-action'), target);
        return;
      }
      /* Tapping the scrim dismisses, which is what the gesture
         implies — but a tap inside the panel must not. */
      if (event.target.classList.contains('gd-sheet__scrim')) closeSheet();
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeSheet();
    });

    /* Paint the cache immediately so a returning parent sees their
       children rather than a spinner, then reconcile with the server. */
    var cached = readCache();
    if (cached) {
      state = cached;
      render();
    }

    if (!CONFIG.api || !CONFIG.token) {
      showFatal('The tracker is not connected yet. Please let us know if you see this.');
      return;
    }

    window.addEventListener('online', function () {
      flushQueue().then(function (didFlush) {
        if (didFlush) {
          toast('Your saved taps have synced.');
          request('GET', '/api/state').then(function (snapshot) {
            state = snapshot;
            writeCache(state);
            render();
          }).catch(function () {});
        }
      });
    });

    flushQueue()
      .then(function () { return request('GET', '/api/state'); })
      .then(function (snapshot) {
        state = snapshot;
        writeCache(state);
        render();
        /* Only worth attempting for a family with nothing yet. */
        if (!state.children.length) return rescueLegacy();
      })
      .catch(function (err) {
        if (state) {
          /* We have a cache — show it and say the truth about it. */
          setOffline(true);
          return;
        }
        showFatal(err.status === 401
          ? 'Please sign out and back in, then try again.'
          : 'We could not reach the tracker. Please check your connection and try again.');
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
